import React, { useState, useRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import Dialog from '~/Common-Objects/UI/Dialog'
import EmailChipInput from '~/Common-Objects/UI/Forms/EmailChipInput'
import Icon from '~/Common-Objects/UI/Icon'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import SelectNewDefault from '~/Common-Objects/UI/Select/Default'
import SquareChip from '~/Common-Objects/UI/Chips/SquareChip.jsx'
import InboxButton from '../../../shared/inboxButton'
import SendButton from '../../../shared/sendButton'

import axios from '~/data/http/axios'
import ComposerComponent from '../../../shared/composer'
import { parseSignature } from '../helpers/signature'
import FindReplyDialog from '../findReply'
import { alterSnackbar } from '../../../../../../../../data/actions/siteActions'
import SmileyPickerTool from '../toolSmileyPicker'
import i18n from '~/i18n'
import { useSelector } from 'react-redux'
import { Tooltip } from 'react-tooltip'
const allowedContentTypes = [
    'image/png',
    'image/gif',
    'iamge/jpeg',
    'image/jpg',
    'image/jpeg',
    'application/pdf',
    'text/csv'
]

export default function Composer({
    className,
    activeConversation = {},
    sentMessage = () => {},
    creatingConversation = null,
    wizardConversation = false,
    closeConversation = () => {},
    sendAgentTyping = () => {},
    userRole = null,
    updateConversations = () => {},
    channels = []
}) {
    const uploader = useRef(null)
    const draftTimer = useRef(null)
    const dispatch = useDispatch()
    const user = useSelector((state) => state.user)
    const origSendTypes = [
        { label: i18n.t('chat:inbox.conversation.composer.send'), value: 'send', messageType: 'REPLY' },
        { label: i18n.t('chat:inbox.conversation.composer.sendClose'), value: 'sendAndClose', messageType: 'REPLY' },
        { label: i18n.t('chat:inbox.conversation.composer.addNote'), value: 'addNote', messageType: 'NOTE' },
        { label: i18n.t('chat:inbox.conversation.composer.addNoteClose'), value: 'addNoteClose', messageType: 'NOTE' }
    ]
    const messageTypes = [
        { value: 'REPLY', label: i18n.t('chat:inbox.conversation.composer.reply'), defaultValue: 'send' },
        { value: 'NOTE', label: i18n.t('chat:inbox.conversation.composer.note'), defaultValue: 'addNote' }
    ]

    const [firstLoad, setFirstLoad] = useState(true)
    const [messageType, setMessageType] = useState('REPLY')
    const [message, setMessage] = useState('')
    const [messageError, setMessageError] = useState('')
    const [buttonType, setButtonType] = useState('send')
    const [sendTypes, setSendTypes] = useState(origSendTypes)
    const [sendButtonLabel, setSendButtonLabel] = useState()
    const [editorDisabled, setEditorDisabled] = useState(false)
    const [editorRef, setEditorRef] = useState()
    const [assignedTo, setAssignedTo] = useState(activeConversation.AccountUserId || undefined)
    const [lastTypedDate, setLastTypedDate] = useState(undefined)
    const [lastSentTypedDate, setLastSentTypedDate] = useState(undefined)
    const [findSavedReplyOpen, setFindSavedReplyOpen] = useState(false)
    const [files, setFiles] = useState([])
    const [sending, setSending] = useState(false)
    const [savingDraft, setSavingDraft] = useState(false)
    const [savedDraft, setSavedDraft] = useState(true)
    const [draft, setDraft] = useState()
    const [signature, setSignature] = useState('')
    const [large, setLarge] = useState(false)

    const [ccOpen, setCcOpen] = useState(false)
    const [carbonCopies, setCarbonCopies] = useState(activeConversation?.carbonCopies || [])

    useEffect(() => {
        init()
        workOutSendOptions()
    }, [])

    useEffect(() => {
        if (lastTypedDate === undefined) return
        sendTyping()
    }, [lastTypedDate])

    useEffect(() => {
        if (firstLoad) return
        if (message == draft && message.length) return
        setSavingDraft(true)
        setSavedDraft(false)

        if (draftTimer.current) clearTimeout(draftTimer.current)
        draftTimer.current = setTimeout(() => {
            if (!message.length) {
                deleteDraft()
            } else {
                saveDraft()
            }
        }, 1000)
    }, [message])
    useEffect(() => {
        let isEmailChannel =
            activeConversation.InboxChannel?.type == 1 ||
            (!activeConversation.vistorWebChatOnline && activeConversation.InboxChannel?.type != 1)
        setCcOpen(isEmailChannel && carbonCopies.length > 0)
    }, [carbonCopies, activeConversation.InboxChannel?.type, activeConversation.vistorWebChatOnline])

    const init = async () => {
        try {
            const draftMessage = await axios.get('/inboxes/chat/' + activeConversation.id + '/draft')
            let finalContent = ''
            if (draftMessage?.data?.content) {
                finalContent = draftMessage.data.content
                setDraft(draftMessage.data.content)
            }

            if (!finalContent) {
                const channel = channels.find((f) => f.id == activeConversation?.InboxChannel?.id)
                if (channel) {
                    const tmpSignature = parseSignature(channel?.settings?.signature, user)
                    if (tmpSignature) {
                        setSignature(tmpSignature)
                        finalContent += '<br/><br/>' + tmpSignature
                    }
                }
            }
            setMessage(finalContent)
            setFirstLoad(false)
        } catch (e) {
            setFirstLoad(false)
        }
    }

    const saveDraft = async () => {
        setSavingDraft(true)
        await axios.put('/inboxes/chat/' + activeConversation.id + '/draft', {
            draftMessage: message
        })
        setSavingDraft(false)
        setSavedDraft(true)
    }

    const deleteDraft = async () => {
        await axios.delete('/inboxes/chat/' + activeConversation.id + '/draft')
        setSavingDraft(false)
        setSavedDraft(true)
    }

    const clearEditor = () => {
        editorRef.current.editorCommands.execCommand('mceSetContent', signature, '')
        setMessage(signature)
        setFiles([])
    }

    const sendMessage = async () => {
        let closing = buttonType == 'close'
        let sendClosing = buttonType == 'sendAndClose' || buttonType == 'addNoteClose'

        if (!closing && (!message || message.length < 2 || message.replace(/&nbsp;/g, ' ').trim().length < 1)) {
            return setMessageError(i18n.t('chat:inbox.conversation.composer.longer'))
        }
        setMessageError('')
        try {
            let newChatLine = undefined
            if (closing) {
                return closeConversation(assignedTo)
            } else {
                setSending(true)
                //clear draft timer
                if (draftTimer.current) clearTimeout(draftTimer.current)
                let postUrl = `/inboxes/chat/${activeConversation.id}`
                if (wizardConversation) {
                    postUrl += `/assistant`
                }
                const promises = []
                for (const file of files) {
                    const formData = new FormData()
                    formData.append('file', file)
                    promises.push(
                        axios.post(`/inboxes/chat/${activeConversation.id}/attachment`, formData, {
                            headers: {
                                'Content-Type': 'multipart/form-data'
                            },
                            onUploadProgress: (progressEvent) => {
                                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                            }
                        })
                    )
                }
                const uploads = await Promise.all(promises)

                newChatLine = await axios.post(postUrl, {
                    text: message,
                    messageType: messageType,
                    actionType: buttonType,
                    assignTo: assignedTo,
                    closing: sendClosing,
                    attachments: uploads.map((u) => u.data.id),
                    carbonCopies: carbonCopies
                })
                setSending(false)
            }
            workOutSendOptions('REPLY')
            clearEditor()
            sentMessage(newChatLine, sendClosing)
        } catch (e) {
            console.log(e)
            setMessageError(e.data?.error ? e.data.error : i18n.t('chat:inbox.conversation.composer.err'))
            setSending(false)
        }
    }
    const sendTyping = async () => {
        if (!activeConversation.vistorWebChatOnline) return
        //only send if the last sent type date is more than five seconds ago, or blank
        if (lastSentTypedDate && new Date().getTime() - lastSentTypedDate.getTime() < 3000) return
        setLastSentTypedDate(new Date())
        sendAgentTyping()
    }

    const changeMessageType = (val) => {
        workOutSendOptions(val)
    }

    const workOutSendOptions = (newMessageType, newButtonType) => {
        let finalMessageType = messageType
        let finalButtonType = buttonType
        if (newMessageType) {
            finalMessageType = newMessageType
            let foundNewMessageType = messageTypes.find((f) => f.value == newMessageType)
            if (foundNewMessageType && foundNewMessageType.defaultValue) {
                finalButtonType = foundNewMessageType.defaultValue
            }
        }
        if (newButtonType) finalButtonType = newButtonType

        let foundOptions = origSendTypes.filter((f) => f.value == 'sendAndClose' || f.value == 'send')
        if (creatingConversation || activeConversation.status === 2 || wizardConversation) {
            foundOptions = origSendTypes.filter((f) => f.value == 'send')
        }
        if (finalMessageType == 'NOTE') {
            foundOptions = origSendTypes.filter((f) => f.value == 'addNote')
            if (activeConversation.status !== 2) {
                foundOptions = origSendTypes.filter((f) => f.value == 'addNote' || f.value == 'addNoteClose')
            }
        }

        let foundButton = foundOptions.find((f) => f.value == finalButtonType)

        let finalButtonLabel = ''
        if (foundButton) {
            finalButtonLabel = foundButton.label
            finalButtonType = foundButton.value
        }

        setSendTypes(foundOptions)
        setSendButtonLabel(finalButtonLabel)
        setButtonType(finalButtonType)
        setMessageType(finalMessageType)
    }

    const addFiles = (event) => {
        const arr = [...files]
        let error = ''
        if (event.target.files?.length) {
            for (const file of event.target.files) {
                if (file.size > 20971520) {
                    error = i18n.t('chat:inbox.conversation.composer.sizeLimit')
                    continue
                }
                arr.push(file)
            }
        }
        if (error) dispatch(alterSnackbar(true, error))
        setFiles(arr)
        uploader.current.value = null
    }

    const removeFile = (index) => {
        const arr = [...files]
        arr.splice(index, 1)
        setFiles(arr)
    }

    if (firstLoad) return null

    let composerHolderClasses = 'inbox-composer__root ' + className
    if (messageType == 'NOTE') composerHolderClasses += ' inbox-composer__variant_note'
    if (editorDisabled) composerHolderClasses += ' inbox-composer__variant_disabled'

    const emailChannel =
        activeConversation.InboxChannel?.type === 1 ||
        (!activeConversation.vistorWebChatOnline &&
            activeConversation.InboxChannel?.type !== 1 &&
            activeConversation.InboxChannel?.type !== 3)

    return (
        <>
            {emailChannel && (
                <>
                    <div
                        className={'inbox-composer__standAloneLine'}
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <div style={{ display: 'flex' }}>
                            <div className={'inbox-composer__standAloneLine_label'}>From:</div>
                            <div className={'inbox-composer__standAloneLine_inputHolder'}>
                                {activeConversation?.InboxChannel?.name}
                            </div>
                        </div>
                        <div>
                            <SquareChip
                                className={ccOpen ? '' : 'square-chip-transparent'}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setCcOpen(!ccOpen)
                                }}
                            >
                                CC ({carbonCopies.length || 0})
                            </SquareChip>
                        </div>
                    </div>
                    {ccOpen == true && (
                        <div
                            className={'inbox-composer__standAloneLine'}
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}
                        >
                            <div style={{ display: 'flex', flex: 1 }}>
                                <div className={'inbox-composer__standAloneLine_label'}>CC:</div>
                                <div
                                    className={'inbox-composer__standAloneLine_inputHolder'}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flex: 1,
                                        maxHeight: 100,
                                        overflow: 'auto'
                                    }}
                                >
                                    <EmailChipInput
                                        value={carbonCopies}
                                        onChange={(value) => {
                                            setCarbonCopies(value)
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            <div className={composerHolderClasses}>
                <ComposerComponent
                    large={large}
                    editorDisabled={editorDisabled || wizardConversation || userRole > 1}
                    message={message}
                    setMessage={setMessage}
                    goClearEditor={clearEditor}
                    setEditorRef={setEditorRef}
                    typing={() => {
                        setLastTypedDate(new Date())
                    }}
                    userRole={userRole}
                    sendMessage={sendMessage}
                    onFilePaste={(files) => addFiles({ target: { files } })}
                />

                <div className={'inbox-composer__actions'}>
                    <div className='inbox-composer__actionsLeft'>
                        {!wizardConversation && !editorDisabled && (
                            <>
                                <InboxButton
                                    variant='ghost'
                                    size='s'
                                    iconOnly
                                    aria-label='Insert attachment'
                                    data-tooltip-content={i18n.t('chat:inbox.conversation.composer.addAttachment')}
                                    data-tooltip-id='attachment'
                                    disabled={userRole > 1}
                                    onClick={() => {
                                        uploader.current.click()
                                    }}
                                >
                                    <Icon name='paper-clip' style={{ width: 20, height: 20 }} />
                                </InboxButton>
                                <input
                                    type='file'
                                    ref={uploader}
                                    style={{ display: 'none' }}
                                    onChange={addFiles}
                                    multiple
                                />
                                <Dialog.Root open={findSavedReplyOpen} onOpenChange={setFindSavedReplyOpen}>
                                    <Dialog.Trigger asChild>
                                        <InboxButton
                                            variant='ghost'
                                            size='s'
                                            iconOnly
                                            aria-label='Saved Replies'
                                            data-tooltip-content={i18n.t(
                                                'chat:inbox.conversation.composer.savedReplies'
                                            )}
                                            data-tooltip-id='template'
                                            disabled={userRole > 1}
                                        >
                                            <Icon name='replies' style={{ width: 20, height: 20 }} />
                                        </InboxButton>
                                    </Dialog.Trigger>
                                    {findSavedReplyOpen && (
                                        <FindReplyDialog
                                            onSelect={(v) => {
                                                if (v && editorRef) {
                                                    // setMessage(v)
                                                    editorRef.current.setContent(v)
                                                }
                                                setFindSavedReplyOpen(false)
                                            }}
                                            inboxId={activeConversation.InboxId}
                                            currentContent={message}
                                        />
                                    )}
                                </Dialog.Root>

                                <SmileyPickerTool
                                    userRole={userRole}
                                    onSelect={(smiley) => {
                                        //add smiley to tinymce content
                                        if (editorRef) {
                                            editorRef.current.insertContent(smiley)
                                        }
                                    }}
                                />
                                <InboxButton
                                    variant='ghost'
                                    size='s'
                                    iconOnly
                                    aria-label={large ? 'Collapse composer' : 'Expand composer'}
                                    data-tooltip-content={
                                        large
                                            ? i18n.t('chat:inbox.conversation.composer.collapse')
                                            : i18n.t('chat:inbox.conversation.composer.expand')
                                    }
                                    data-tooltip-id='composer-size'
                                    onClick={() => setLarge(!large)}
                                    disabled={userRole > 1}
                                >
                                    <Icon
                                        name={large ? 'collapse' : 'expand'}
                                        style={{ width: 20, height: 20 }}
                                    />
                                </InboxButton>

                                {(!savedDraft || !!message) && <DraftStatus saved={savedDraft} saving={savingDraft} />}
                            </>
                        )}
                    </div>

                    <div className='inbox-composer__actionsRight'>
                        {messageError && messageError.length > 0 && <div className='text-red'>{messageError}</div>}
                        {sending && <div className='text-grey loading'>Sending</div>}

                        {!wizardConversation && (
                            <SelectNewDefault
                                disabled={userRole > 1}
                                value={messageType}
                                data={messageTypes}
                                align='end'
                                triggerProps={{ size: 's', variant: 'ghost' }}
                                onValueChange={changeMessageType}
                                popupStyle={{ minWidth: 0 }}
                                fullWidth={false}
                            />
                        )}

                        <SendButton
                            size='s'
                            onClick={sendMessage}
                            dropDownOptions={sendTypes}
                            dropDownSelected={buttonType}
                            dropDownChange={(value) => {
                                workOutSendOptions(undefined, value)
                            }}
                            disabled={wizardConversation || userRole > 1 || sending}
                            title={wizardConversation ? i18n.t('chat:inbox.pleaseAddAChannelFirst') : ''}
                            popupAlign='end'
                            popupMinWidth={0}
                        >
                            {sendButtonLabel}
                        </SendButton>
                    </div>
                </div>
            </div>

            {files.length > 0 && (
                <div className='inbox-compose__attachments'>
                    {Array.from(files).map((file, index) => {
                        return (
                            <div key={index} className='inbox-compose__attachments__attachment'>
                                <div className='inbox-compose__attachments__attachment_label'>{file.name}</div>
                                <div className='inbox-compose__attachments__attachment_size'>
                                    {humanizeBytes(file.size)}
                                </div>
                                <Icons
                                    icon='cross'
                                    style={{ height: 16, width: 16 }}
                                    color='#595C61'
                                    onClick={() => removeFile(index)}
                                />
                            </div>
                        )
                    })}
                </div>
            )}
        </>
    )
}

function humanizeBytes(bytes) {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    if (bytes === 0) return '0 Byte'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i]
}

function DraftStatus({ saved = false, saving = false }) {
    if (saving)
        return (
            <div className='inbox-center__composer__draft'>
                <LoadingIcons style={{ height: 20, width: 20 }} />
            </div>
        )
    if (saved)
        return (
            <div
                className='inbox-center__composer__draft'
                aria-label={i18n.t('chat:inbox.conversation.composer.draftSaved')}
                data-tooltip-id='draft_status'
                data-tooltip-content={i18n.t('chat:inbox.conversation.composer.draftSaved')}
            >
                <Tooltip id='draft_status' />
                <Icons icon='tickCircle' />
            </div>
        )
    return null
}
